import { useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import {
  Building2,
  Eye,
  EyeOff,
  Info,
  KeyRound,
  Lock,
  Mail,
  Phone,
  UserRound,
  Users,
} from 'lucide-react'
import { useWatch, type UseFormRegister, type FieldErrors, type UseFormSetValue, type UseFormTrigger, type Control } from 'react-hook-form'
import { cn } from '@lib/utils/helpers'
import { Tooltip } from '@/components/ui/Tooltip'
import { SSNInput } from '@/components/ui/SSNInput'
import { DatePicker } from '@components/ui/DatePicker'
import { formatPhoneNumber } from '@/types/application'
import { filterPhoneInput } from '@/lib/utils/formatters'
import { strengthBarColors } from '../utils/passwordStrength'
import type { InviteData } from '@/types/auth'
import type { VerifyInfoFormData } from '../types'
import {
  inviteCheckboxClass,
  inviteInputFocusClass,
  inviteSubmitButtonClass,
} from './inviteOnboardingUi'

type CoApplicantRow = {
  first_name?: string
  last_name?: string
  email?: string
  relationship_type?: string
  mobile_no?: string
}

type InviteRegistrationFormProps = {
  invitationData: InviteData | null
  inviteCode: string
  autoFilledFields: Set<string>
  control: Control<VerifyInfoFormData>
  register: UseFormRegister<VerifyInfoFormData>
  errors: FieldErrors<VerifyInfoFormData>
  setValue: UseFormSetValue<VerifyInfoFormData>
  trigger: UseFormTrigger<VerifyInfoFormData>
  isLoading: boolean
  isSubmitting: boolean
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  t: (key: string) => string
  /** When true, omit the outer full-page chrome (used inside InviteOnboardingLayout). */
  embedded?: boolean
}

export function listedCoApplicantCount(raw: InviteData['co_applicant']): number {
  return parseCoApplicants(raw).length
}

function parseCoApplicants(raw: InviteData['co_applicant']): CoApplicantRow[] {
  if (!raw) return []
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    const rows = Array.isArray(parsed)
      ? parsed
      : parsed && typeof parsed === 'object'
        ? Object.values(parsed)
        : []
    return rows.filter(
      (person): person is CoApplicantRow =>
        Boolean(person) &&
        typeof person === 'object' &&
        Boolean(
          (person as CoApplicantRow).first_name ||
            (person as CoApplicantRow).last_name ||
            (person as CoApplicantRow).email ||
            (person as CoApplicantRow).mobile_no,
        ),
    )
  } catch {
    return []
  }
}

function formatRent(value?: number | string) {
  if (value == null || value === '') return ''
  const amount = Number(value)
  if (Number.isNaN(amount)) return String(value)
  return `$${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}/mo`
}

/** Number only the first list — same as mobile; avoids stepping footer/extra `<ul>` blocks in org HTML. */
function numberIntroSteps(html: string) {
  const listMatch = html.match(/<(ul|ol)\b[^>]*>[\s\S]*?<\/\1>/i)
  if (!listMatch) return html
  const listHtml = listMatch[0]
  let step = 0
  const numberedList = listHtml.replace(/<li(\s[^>]*)?>/gi, (match) => {
    step += 1
    return `${match}<span class="mr-1.5 font-medium">${step}.</span>`
  })
  return html.replace(listHtml, numberedList)
}

function formatMoveIn(value?: string) {
  if (!value) return ''
  const normalized = value.includes('T') ? value : value.replace(' ', 'T')
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const SUPPORT_PHONE = '(877) 581-7368'
const SUPPORT_EMAIL = 'help@libertyrent.com'

const fieldClass = (invalid: boolean, locked: boolean, loading: boolean) =>
  cn(
    `w-full rounded-lg border bg-white px-3.5 py-2.5 text-sm ${inviteInputFocusClass}`,
    invalid ? 'border-red-500' : 'border-gray-300',
    locked && 'cursor-default bg-gray-100 text-gray-700',
    loading && 'cursor-not-allowed opacity-50',
  )

export function InviteRegistrationForm({
  invitationData,
  inviteCode,
  autoFilledFields,
  control,
  register,
  errors,
  setValue,
  trigger,
  isLoading,
  isSubmitting,
  onSubmit,
  t,
  embedded = false,
}: InviteRegistrationFormProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordReadonly, setPasswordReadonly] = useState(true)
  const [confirmPasswordReadonly, setConfirmPasswordReadonly] = useState(true)

  const dob = useWatch({ control, name: 'dob' })
  const ssn = useWatch({ control, name: 'ssn' })
  const email = useWatch({ control, name: 'email' })
  const mobilePhone = useWatch({ control, name: 'mobilePhone' })
  const communicationPreference = useWatch({ control, name: 'communicationPreference' })
  const password = useWatch({ control, name: 'password' })
  const strengthColors = useMemo(() => strengthBarColors(password || ''), [password])

  const coApplicants = parseCoApplicants(invitationData?.co_applicant)
  const address =
    invitationData?.property_street_address ||
    [
      invitationData?.property_address,
      invitationData?.property_city,
      invitationData?.property_state,
      invitationData?.property_zipcode,
    ]
      .filter(Boolean)
      .join(', ')
  const form = (
        <form onSubmit={onSubmit} autoComplete="off" className="w-full space-y-5">
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-primary">
              <Info className="h-4 w-4 shrink-0" aria-hidden />
              Before You Begin
            </h2>
            <div className="mt-4 rounded-lg border border-[#c9f6ec] bg-[#EBFFFA] px-5 py-4">
              <div
                className="text-sm leading-6 text-slate-700 [&_h3]:mb-3 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-slate-900 [&_li]:list-none [&_ol]:m-0 [&_ol]:grid [&_ol]:list-none [&_ol]:grid-cols-1 [&_ol]:gap-x-10 [&_ol]:gap-y-2 [&_ol]:p-0 sm:[&_ol]:grid-cols-2 [&_p]:mb-3 [&_ul]:m-0 [&_ul]:grid [&_ul]:list-none [&_ul]:grid-cols-1 [&_ul]:gap-x-10 [&_ul]:gap-y-2 [&_ul]:p-0 sm:[&_ul]:grid-cols-2"
                dangerouslySetInnerHTML={{
                  __html: numberIntroSteps(invitationData?.invite_intro_html || ''),
                }}
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
              <span>Questions? Liberty Rent Customer Service</span>
              <a href="tel:8775817368" className="inline-flex items-center gap-1.5 font-medium text-primary hover:text-button">
                <Phone className="h-3.5 w-3.5" aria-hidden />
                {SUPPORT_PHONE}
              </a>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-1.5 font-medium text-primary hover:text-button">
                <Mail className="h-3.5 w-3.5" aria-hidden />
                {SUPPORT_EMAIL}
              </a>
            </div>
          </section>

          <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[1.45fr_1fr]">
            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-5">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <UserRound className="h-4 w-4 shrink-0" aria-hidden />
                  Personal &amp; Contact Information
                </h2>
              </div>

              <p className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900">
                <strong className="font-semibold">{t('verifyInfo.legalNameNoteLabel')}</strong>{' '}
                {t('verifyInfo.legalNameNote')}
              </p>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label htmlFor="firstName" className="mb-1.5 block text-sm font-medium text-slate-700">
                    First Name
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    {...register('firstName')}
                    readOnly={autoFilledFields.has('firstName')}
                    disabled={isLoading}
                    className={fieldClass(Boolean(errors.firstName), autoFilledFields.has('firstName'), isLoading)}
                  />
                  {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName.message}</p>}
                </div>
                <div>
                  <label htmlFor="middleName" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Middle Name
                  </label>
                  <input
                    id="middleName"
                    type="text"
                    placeholder="Optional"
                    {...register('middleName')}
                    disabled={isLoading}
                    className={fieldClass(Boolean(errors.middleName), false, isLoading)}
                  />
                  {errors.middleName && <p className="mt-1 text-xs text-red-600">{errors.middleName.message}</p>}
                </div>
                <div>
                  <label htmlFor="lastName" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Last Name
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    {...register('lastName')}
                    readOnly={autoFilledFields.has('lastName')}
                    disabled={isLoading}
                    className={fieldClass(Boolean(errors.lastName), autoFilledFields.has('lastName'), isLoading)}
                  />
                  {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName.message}</p>}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,3fr)_minmax(0,6fr)]">
                <div>
                  <label htmlFor="inputbirthdate" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Date of Birth
                    <span className="text-red-500">*</span>
                  </label>
                  <DatePicker
                    id="inputbirthdate"
                    name="birthdate"
                    autoComplete="bday"
                    value={dob || ''}
                    onChange={(value) => {
                      setValue('dob', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
                    }}
                    onBlur={() => {
                      void trigger('dob')
                    }}
                    showHelperText={false}
                    disabled={isLoading}
                    error={errors.dob?.message}
                    dataTestId="verify-info-dob"
                    placeholder="MM/DD/YYYY"
                    className="px-3.5 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="verify-info-ssn" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Social Security Number (SSN) / Individual Taxpayer ID (ITIN)
                    <span className="text-red-500">*</span>
                  </label>
                  <SSNInput
                    value={ssn || ''}
                    onChange={(value) => {
                      setValue('ssn', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
                    }}
                    disabled={isLoading}
                    className={fieldClass(Boolean(errors.ssn), false, isLoading)}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Your SSN or ITIN is required for the background and credit check. It is stored securely and encrypted.
                  </p>
                  {errors.ssn && <p className="mt-1 text-xs text-red-600">{errors.ssn.message}</p>}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="inputEmail" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Email
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="inputEmail"
                    type="text"
                    autoComplete="nope"
                    {...register('email')}
                    readOnly={Boolean(invitationData?.email)}
                    disabled={isLoading}
                    className={fieldClass(Boolean(errors.email), Boolean(invitationData?.email), isLoading)}
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
                </div>
                <div>
                  <label htmlFor="inputContact" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Phone Number
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="inputContact"
                    type="text"
                    autoComplete="nope"
                    name="mobile_no"
                    value={mobilePhone || ''}
                    onChange={(event) => {
                      setValue('mobilePhone', filterPhoneInput(event.target.value), { shouldValidate: true })
                    }}
                    onBlur={() => {
                      void trigger('mobilePhone')
                    }}
                    readOnly={Boolean(invitationData?.mobile_no)}
                    disabled={isLoading}
                    className={fieldClass(
                      Boolean(errors.mobilePhone),
                      Boolean(invitationData?.mobile_no),
                      isLoading,
                    )}
                  />
                  {errors.mobilePhone && (
                    <p className="mt-1 text-xs text-red-600">{errors.mobilePhone.message}</p>
                  )}
                </div>
              </div>

              <label htmlFor="shared_phone_no" className="mt-4 flex items-center gap-2 text-sm text-slate-600">
                <input
                  id="shared_phone_no"
                  type="checkbox"
                  {...register('isSharedPhone')}
                  disabled={isLoading}
                  className={inviteCheckboxClass}
                />
                This is a shared phone number.
              </label>

              <div className="mt-5">
                <p className="mb-2 text-sm font-medium text-slate-700">
                  Communication Preference
                  <span className="text-red-500">*</span>
                </p>
                <div
                  className={cn(
                    'inline-flex w-fit flex-wrap gap-2 rounded-lg',
                    errors.communicationPreference && 'ring-2 ring-red-500 ring-offset-2',
                  )}
                >
                  {([
                    ['Email', 'Email'],
                    ['Text Msg', 'Text Message'],
                  ] as const).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() =>
                        setValue('communicationPreference', value, {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        })
                      }
                      className={cn(
                        'inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium',
                        communicationPreference === value
                          ? 'border-button bg-button text-white'
                          : 'border-slate-300 bg-white text-slate-700',
                      )}
                    >
                      {value === 'Email' ? <Mail className="h-4 w-4" aria-hidden /> : null}
                      {label}
                    </button>
                  ))}
                </div>
                {errors.communicationPreference && (
                  <p className="mt-1 text-xs text-red-600">{errors.communicationPreference.message}</p>
                )}
              </div>

              <p className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-600">
                <strong className="font-semibold text-slate-800">{t('verifyInfo.privacyNoteLabel')}</strong>{' '}
                {t('verifyInfo.privacyNote')}
              </p>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-5 flex items-start justify-between gap-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <Building2 className="h-4 w-4 shrink-0" aria-hidden />
                  Your Property
                </h2>
                <span className="text-sm font-medium text-slate-700">Invitation details</span>
              </div>

              <div className="flex items-start justify-between gap-3 pb-4">
                <div>
                  <p className="text-lg font-semibold text-slate-900">
                    {invitationData?.property_name || 'Your Property'}
                  </p>
                  {address && <p className="mt-1 text-sm text-slate-500">{address}</p>}
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-700">Monthly Rent</p>
                  <p className="text-lg font-semibold text-emerald-600">
                    {formatRent(invitationData?.rent_amount) || '—'}
                  </p>
                  {invitationData?.lease_start_date && (
                    <p className="text-xs text-slate-500">
                      Move-in: {formatMoveIn(invitationData.lease_start_date)}
                    </p>
                  )}
                </div>
              </div>

              <dl className="grid grid-cols-2 gap-4 border-t border-slate-200 py-4 text-sm">
                <div>
                  <dt className="text-sm font-medium text-slate-700">Invite Code</dt>
                  <dd className="mt-1 font-medium text-slate-800">{inviteCode || '—'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-700">Leasing Agent</dt>
                  <dd className="mt-1 font-medium text-slate-800">{invitationData?.agent_name || '—'}</dd>
                  {invitationData?.agent_email && (
                    <dd className="text-xs text-slate-500">{invitationData.agent_email}</dd>
                  )}
                </div>
              </dl>

              {coApplicants.length > 0 ? (
                <div className="border-t border-slate-200 py-4">
                  <p className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Users className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                    Co-Applicants / Roommates
                  </p>
                  <div className="grid grid-cols-4 gap-3 border-b border-slate-200 pb-2 text-sm font-medium text-slate-700">
                    <span>Name</span>
                    <span>Relationship</span>
                    <span>Email</span>
                    <span>Phone</span>
                  </div>
                  {coApplicants.map((person, index) => (
                    <div
                      key={`${person.email || person.mobile_no || index}`}
                      className="grid grid-cols-1 gap-2 border-b border-slate-200 py-3 text-sm last:border-b-0 sm:grid-cols-4 sm:gap-3"
                    >
                      <p className="text-slate-800">
                        {[person.first_name, person.last_name].filter(Boolean).join(' ') || '—'}
                      </p>
                      <p className="text-slate-700">{person.relationship_type || '—'}</p>
                      <p className="break-all text-slate-700">{person.email || '—'}</p>
                      <p className="text-slate-700">
                        {formatPhoneNumber(person.mobile_no) || person.mobile_no || '—'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center justify-between gap-4">
                  <label htmlFor="expected-residents" className="text-sm font-medium text-slate-700">
                    Expected Resident(s) (including children and other roommates)
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="expected-residents"
                    {...register('expectedResidents')}
                    disabled={isLoading}
                    className={cn(
                      'w-28 rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none',
                      errors.expectedResidents
                        ? 'border-red-500 ring-2 ring-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                        : cn('border-slate-300', inviteInputFocusClass),
                    )}
                  >
                    <option value="">Select</option>
                    {Array.from({ length: 9 }, (_, index) => String(index + 1)).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-2 text-xs text-slate-500">This field is required to proceed.</p>
                {errors.expectedResidents && (
                  <p className="mt-1 text-xs text-red-600">{errors.expectedResidents.message}</p>
                )}
              </div>
            </section>
          </div>

          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-primary">
                <KeyRound className="h-4 w-4 shrink-0" aria-hidden />
                Create Account
              </h2>
              <span className="text-xs font-medium text-slate-700">Secure login</span>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label htmlFor="login-email" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Email (Login)
                </label>
                <div className="relative">
                  <input
                    id="login-email"
                    type="text"
                    value={email || ''}
                    readOnly
                    className="w-full cursor-default rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 pr-9 text-sm text-slate-700"
                  />
                  <Lock className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
                </div>
              </div>
              <div>
                <label htmlFor="inputPassword" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Password
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="inputPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Create a password"
                    readOnly={passwordReadonly}
                    {...register('password')}
                    onFocus={() => setPasswordReadonly(false)}
                    disabled={isLoading}
                    className={cn(fieldClass(Boolean(errors.password), false, isLoading), 'pr-10')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
                <p className="mt-1.5 text-xs text-slate-500">Password must be at least 6 characters.</p>
                {password && (
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-500" aria-live="polite">
                    <span>{t('verifyInfo.passwordStrength')}:</span>
                    <ul className="m-0 flex list-none items-center gap-px p-0">
                      {strengthColors.map((color, index) => (
                        <li key={index} className="inline-block h-[5px] w-5 rounded-sm" style={{ backgroundColor: color }} />
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="inputConfirmPassword" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Confirm Password
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="inputConfirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Re-enter password"
                    readOnly={confirmPasswordReadonly}
                    {...register('confirmPassword')}
                    onFocus={() => setConfirmPasswordReadonly(false)}
                    disabled={isLoading}
                    className={cn(fieldClass(Boolean(errors.confirmPassword), false, isLoading), 'pr-10')}
                  />
                  <Tooltip content={showConfirmPassword ? 'Hide password' : 'Show password'} side="top">
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                  </Tooltip>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>
          </section>

          <div className="flex flex-col items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={isLoading || isSubmitting}
              className={inviteSubmitButtonClass}
            >
              {isLoading || isSubmitting ? t('verifyInfo.submitting') : t('verifyInfo.submit')}
            </button>
            <Link to="/login" className="text-sm font-medium text-primary hover:text-button">
              {t('verifyInfo.signInLink')}
            </Link>
          </div>

          <p className="pt-2 text-center text-xs leading-relaxed text-slate-400">
            {t('verifyInfo.emailNote')}{' '}
            <a href="mailto:notifications@libertyrent.io" className="font-medium text-primary hover:text-button">
              notifications@libertyrent.io
            </a>
          </p>
        </form>
  )

  if (embedded) {
    return form
  }

  return (
    <div className="min-h-screen bg-[#f4f7fb]">
      <div className="mx-auto flex min-h-screen w-full max-w-7xl items-start justify-center px-4 py-8 pb-16 sm:px-6">
        {form}
      </div>
    </div>
  )
}
